import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  FileText,
  KeyRound,
  Mail,
  Search,
  Settings2,
  ShieldCheck,
  UserRound
} from "lucide-react";
import type { AuditLog } from "../types";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Avatar, Badge, EmptyState, Loading } from "../components/ui";

function eventIcon(type: string) {
  if (type.toLowerCase().includes("password")) return <KeyRound size={17} />;
  if (type.toLowerCase().includes("user")) return <UserRound size={17} />;
  if (type.toLowerCase().includes("template")) return <FileText size={17} />;
  if (type.toLowerCase().includes("setting")) return <Settings2 size={17} />;
  if (type.toLowerCase().includes("email") || type.toLowerCase().includes("account"))
    return <Mail size={17} />;
  return <ShieldCheck size={17} />;
}

export default function AuditPage({
  notify
}: {
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
}) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .audit()
      .then(setLogs)
      .catch((error) => notify("error", "Couldn’t load audit activity", error.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      logs.filter((log) =>
        `${log.eventType} ${log.userDisplayName} ${log.description}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [logs, search]
  );

  return (
    <div className="page audit-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Accountability</span>
          <h1>Audit activity</h1>
          <p>A tamper-resistant history of important access, message, and configuration actions.</p>
        </div>
      </div>
      <div className="content-toolbar">
        <label className="search-field">
          <Search size={16} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search activity"
          />
        </label>
        <Badge tone="success">
          <Activity size={13} /> Live logging
        </Badge>
      </div>
      {loading ? (
        <Loading label="Loading activity…" />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Activity />} title="No matching activity" message="Try a different search." />
      ) : (
        <section className="audit-timeline panel">
          {filtered.map((log, index) => (
            <article key={log.id}>
              <span className={`audit-icon audit-tone-${index % 4}`}>{eventIcon(log.eventType)}</span>
              <div>
                <header>
                  <strong>{log.eventType.replace(/([a-z])([A-Z])/g, "$1 $2")}</strong>
                  <time>{formatDateTime(log.createdAt)}</time>
                </header>
                <p>{log.description}</p>
                <span className="audit-user">
                  <Avatar name={log.userDisplayName ?? "System"} size="sm" tone={index} />
                  {log.userDisplayName ?? "System"}
                </span>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
